import { MediaKind } from 'modules/manager/domain/media-kind';

export type MediaGenerationJobStatus =
  | 'PENDING'
  | 'RENDERING'
  | 'READY'
  | 'FAILED'
  | 'SKIPPED'
  | 'DISCARDED'
  | 'CANCELLED'
  | 'PUBLISHED';

export interface MediaGenerationJob {
  id: number;
  kind: MediaKind;
  subjectType: string;
  subjectKey: string;
  subjectTitle: string;
  status: MediaGenerationJobStatus;
  triggerSource: string;
  championship: { id: number; name: string };
  template: { id: number; name: string } | null;
  image: { id: number; type: string | null; externalUrl: string } | null;
  text: string | null;
  error: string | null;
  attempts: number;
  scheduledAt: string;
  processedAt: string | null;
  createdAt: string | null;
}

export interface MediaGenerationJobsPage {
  items: MediaGenerationJob[];
  total: number;
  page: number;
  perPage: number;
}

export interface GenerateMediaInput {
  championshipId: number;
  kind: MediaKind;
  subjectId: number;
  templateId?: number;
  backgroundUrls?: string[];
}

export interface MediaGenerationJobsInput {
  tournamentId: number;
  championshipId?: number;
  statuses?: string[];
  kinds?: MediaKind[];
  page?: number;
  perPage?: number;
}

const JOB_FIELDS = `
  id
  kind
  subjectType
  subjectKey
  subjectTitle
  status
  triggerSource
  championship { id name }
  template { id name }
  image { id type externalUrl }
  text
  error
  attempts
  scheduledAt
  processedAt
  createdAt
`;

export const GENERATE_MEDIA_MUTATION = `#graphql
  mutation generateMedia($input: GenerateMediaInput!) {
    generateMedia(input: $input) {
      ${JOB_FIELDS}
    }
  }
`;

export const MEDIA_GENERATION_JOBS_QUERY = `#graphql
  query mediaGenerationJobs($input: MediaGenerationJobsInput!) {
    mediaGenerationJobs(input: $input) {
      items { ${JOB_FIELDS} }
      total
      page
      perPage
    }
  }
`;

export const DISCARD_MEDIA_GENERATION_JOB_MUTATION = `#graphql
  mutation discardMediaGenerationJob($jobId: Int!) {
    discardMediaGenerationJob(jobId: $jobId) { status }
  }
`;

export const RETRY_MEDIA_GENERATION_JOB_MUTATION = `#graphql
  mutation retryMediaGenerationJob($jobId: Int!) {
    retryMediaGenerationJob(jobId: $jobId) {
      ${JOB_FIELDS}
    }
  }
`;

export const CANCEL_MEDIA_GENERATION_JOB_MUTATION = `#graphql
  mutation cancelMediaGenerationJob($jobId: Int!) {
    cancelMediaGenerationJob(jobId: $jobId) { status }
  }
`;

export const DELETE_MEDIA_GENERATION_JOB_MUTATION = `#graphql
  mutation deleteMediaGenerationJob($jobId: Int!) {
    deleteMediaGenerationJob(jobId: $jobId) { status }
  }
`;

export const CHAMPIONSHIP_MEDIA_SUBJECTS_QUERY = `#graphql
  query championshipMediaSubjects($id: Int!) {
    championship(id: $id) {
      id
      name
      stages {
        id
        name
        rounds {
          id
          name
          matches {
            id
            date
            teams { id name }
            round { id name }
          }
        }
        teamGroups { id name }
      }
    }
  }
`;

export interface ChampionshipMediaSubjectMatch {
  id: number;
  date: string | null;
  teams: { id: number; name: string }[];
  round: { id: number; name: string } | null;
}

export interface ChampionshipMediaSubjects {
  id: number;
  name: string;
  matches: ChampionshipMediaSubjectMatch[];
  rounds: { id: number; name: string }[];
  stages: { id: number; name: string }[];
  groups: { id: number; name: string }[];
}
